import type { ConventionReportSummary } from '@/services/useConventionReport';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList, Legend } from 'recharts';

interface GuestSectionProps {
  data: ConventionReportSummary;
  onCategoryClick?: (categoryId: string) => void;
}

const DEFAULT_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#e91e63', '#9c27b0'];

export function GuestSection({ data, onCategoryClick }: GuestSectionProps) {
  const { event_guests } = data;

  const categoryData = event_guests.per_category.map(c => ({
    name: c.name,
    value: c.total,
    color: c.color_hex || '#8884d8'
  }));

  const genderData = [
    { name: 'Laki-laki', value: event_guests.per_gender.male || 0 },
    { name: 'Perempuan', value: event_guests.per_gender.female || 0 },
  ].filter(d => d.value > 0);

  const institutionData = event_guests.per_institution.map(i => ({
    name: i.institution,
    total: i.total,
  }));

  return (
    <div className="space-y-6">
      {/* Kartu Kategori */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {event_guests.per_category.map((c) => (
          <div 
            key={c.id} 
            onClick={() => onCategoryClick && onCategoryClick(String(c.id))}
            className="bg-white dark:bg-slate-900 p-5 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group flex flex-col justify-between min-h-[110px]"
          >
            <div 
              className="absolute top-0 right-0 w-24 h-24 rounded-full -mr-8 -mt-8 opacity-10 group-hover:opacity-20 transition-opacity"
              style={{ backgroundColor: c.color_hex || '#6366F1' }}
            />
            <div className="flex items-start gap-2 mb-2 z-10">
              <div className="w-3 h-3 rounded-full mt-1 shrink-0" style={{ backgroundColor: c.color_hex || '#6366F1' }} />
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest leading-tight" title={c.name}>{c.name}</p>
            </div>
            <p className="text-3xl font-black text-slate-900 z-10">{c.total}</p>
            <div className="text-[10px] text-slate-400 font-medium mt-1 z-10">
              Tercetak: <span className="text-emerald-500 font-bold">{c.is_printed}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Distribusi Kategori Kartu */}
        <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gradient-to-br from-amber-50/50 to-orange-50/50 dark:from-amber-900/10 dark:to-orange-900/10 border-amber-100 dark:border-amber-900/30">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Berdasarkan Kategori</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 pt-2">
              {categoryData.map((cat, idx) => {
                const percentage = event_guests.total > 0 ? (cat.value / event_guests.total) * 100 : 0;
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-slate-700">{cat.name}</span>
                      <span className="font-bold text-slate-900">{cat.value} <span className="text-slate-400 font-normal text-xs ml-1">({percentage.toFixed(1)}%)</span></span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: cat.color
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Distribusi Gender */}
        <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gradient-to-br from-rose-50/50 to-pink-50/50 dark:from-rose-900/10 dark:to-pink-900/10 border-rose-100 dark:border-rose-900/30">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Distribusi Gender</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 pt-2">
              {genderData.map((gender, idx) => {
                const totalGender = genderData.reduce((acc, curr) => acc + curr.value, 0);
                const percentage = totalGender > 0 ? (gender.value / totalGender) * 100 : 0;
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: DEFAULT_COLORS[(idx + 2) % DEFAULT_COLORS.length] }} 
                        />
                        <span className="font-medium text-slate-700">{gender.name}</span>
                      </div>
                      <span className="font-bold text-slate-900">{gender.value} <span className="text-slate-400 font-normal text-xs ml-1">({percentage.toFixed(1)}%)</span></span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: DEFAULT_COLORS[(idx + 2) % DEFAULT_COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {/* Top 10 Institusi */}
        <Card className="md:col-span-2 overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gradient-to-br from-cyan-50/50 to-blue-50/50 dark:from-cyan-900/10 dark:to-blue-900/10 border-cyan-100 dark:border-cyan-900/30">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Semua Instansi / Institusi</CardTitle>
          </CardHeader>
          <CardContent className="h-[500px] overflow-y-auto custom-scrollbar">
            <div style={{ height: Math.max(500, institutionData.length * 40) }} className="w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={institutionData}
                  layout="vertical"
                  margin={{ top: 5, right: 50, left: 100, bottom: 5 }}
                >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip
                  formatter={(value) => [`${value} Orang`, 'Total']}
                  cursor={{ fill: 'transparent' }}
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
                <Legend />
                <Bar dataKey="total" name="Total Tamu/Panitia" fill="#00C49F">
                  <LabelList dataKey="total" position="right" style={{ fontSize: '11px', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
